import { makeApiPartners, makeFileApi } from 'src/http/api/internal/internal-http';
import { CompaniesRepository } from 'src/repositories/companies/companies.repository';
import { FileRepository } from 'src/repositories/files/file.repository';
import { Company, CompanyCreate } from 'src/types/companies';
import { create } from 'zustand';
import { cnpj } from 'cpf-cnpj-validator';

type States = {
  loadingPage: boolean;
  companies: Company[];
  loadingImport: boolean;
  redeems: {
    _id: string;
    name: string;
    origin: string;
  }[];
};

type CreateUpdate = {
  error: boolean;
  message: string;
};

type Actions = {
  setLoadingPage: (loading: boolean) => void;
  fetchCompanies: () => Promise<void>;
  importCompanies: (companies: any) => Promise<void>;
  getUrlFile: (file: string) => Promise<string>;
  create: (company: CompanyCreate) => Promise<CreateUpdate>;
  getFreeRedeems: () => Promise<void>;
  getById: (id: string, callback: any, otherSet: any, setGiftbackRedeem: any) => Promise<void>;
  update: (
    company: CompanyCreate & {
      oldRedeems: string[];
    },
    id: string
  ) => Promise<CreateUpdate>;
};

type Companies = States & Actions;

const initialState = {
  loadingPage: false,
  loadingImport: false,
  companies: [],
  redeems: [],
};

const repository = new CompaniesRepository(makeApiPartners());
const fileRepository = new FileRepository(makeFileApi());

export const useCompaniesStore = create<Companies>((set) => ({
  ...initialState,
  setLoadingPage: (loading) => set({ loadingPage: loading }),
  fetchCompanies: async () => {
    set({ loadingPage: true });
    const response = await repository.getCompanies();
    if (response.isLeft()) {
      set({ companies: [], loadingPage: false });
      return;
    }
    const { value } = response;
    const companies = value.map((v: any) => ({
      _id: v._id,
      name: v.name,
      cnpj: cnpj.format(v.cnpj),
      status: v.status,
      image: v.image || v.logo || null,
    }));
    set({ companies: companies || ([] as Company[]), loadingPage: false });
  },

  importCompanies: async (companies: any) => {
    set({ loadingImport: true });
    const response = await repository.importCompanies(companies);
    if (response.isLeft()) {
      set({ loadingImport: false });
      return;
    }
    set({ loadingImport: false });
  },

  getUrlFile: async (file) => {
    const response = await fileRepository.upload(file);
    if (response.isLeft()) {
      return 'error';
    }
    return response.value.data.url;
  },

  create: async (company) => {
    const response = await repository.create(company);
    if (response.isLeft()) {
      return {
        error: true,
        message: response?.value?.message || 'Erro ao criar empresa.',
      };
    }
    return {
      error: false,
      message: '',
    };
  },

  getFreeRedeems: async () => {
    const response = await repository.getFreeRedeems();
    if (response.isLeft()) {
      return;
    }

    set({ redeems: response.value });
  },

  getById: async (id, callback, otherSet, setGiftbackRedeem) => {
    set({ loadingPage: true });
    const response = await repository.getCompanyById(id);

    if (response.isLeft()) {
      set({ loadingPage: false });
      return;
    }
    const { value } = response;
    const { company, redeems } = value;
    callback('name', company.name);
    callback('description', company.description);
    callback('status', company.status);
    callback('image', company.image);
    callback('cnpj', company.cnpj);
    callback('isStore', company.isStore);
    callback('location', company.location);
    callback('isGiftback', !!company?.isGiftback);
    callback('crmCnpj', company.crmCnpj || '');
    callback('adsCnpj', company.adsCnpj || '');
    callback('redeemIds', redeems.map((r: any) => r._id) || []);
    callback(
      'allCnpjs',
      company.allCnpjs.filter(
        (c: any) => ![company.cnpj, company.crmCnpj, company.adsCnpj].includes(c)
      )
    );
    callback('pointsConversionCategory', company.pointsConversionCategory || '1_1');
    otherSet(redeems || []);
    const giftback = redeems.find((r: any) => r.origin === 'giftback');
    if (giftback) {
      setGiftbackRedeem({ name: giftback.name, status: giftback.status, _id: giftback._id });
    }
    set({ loadingPage: false });
  },

  update: async (company, id) => {
    const response = await repository.update(company, id);
    if (response.isLeft()) {
      return {
        error: true,
        message: response?.value?.message || 'Erro ao atualizar empresa.',
      };
    }
    return {
      error: false,
      message: '',
    };
  },
}));
